<template>
  <div class="h-full flex flex-col min-h-0">
    <div class="flex-shrink-0">
      <TableToolbar
        :columns="COLUMNS"
        v-model:search-query="searchQuery"
        v-model:active-filters="activeFilters"
        v-model:visible-keys="visibleKeys"
        v-model:column-order="columnOrder"
        :is-loading="isLoading"
        search-placeholder="Search student, instrument…"
        @refresh="fetchLessons"
        @reset-columns="resetColumns"
      >
        <template #count>
          {{ filteredLessons.length }} of {{ lessons.length }} lesson{{ lessons.length === 1 ? '' : 's' }}
        </template>
      </TableToolbar>
    </div>

    <div class="flex-1 min-h-0 bg-surface-raised dark:bg-surface-raised-dark rounded-lg border border-stroke dark:border-stroke-dark overflow-hidden">
      <div class="h-full overflow-auto">
        <table class="min-w-full divide-y divide-stroke dark:divide-stroke-dark">
          <thead class="sticky top-0 z-10 bg-surface-muted dark:bg-gray-900">
            <tr>
              <th
                v-for="col in activeColumns"
                :key="col.key"
                @click="toggleSort(col.key)"
                class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group hover:bg-surface-muted dark:hover:bg-surface-muted-dark transition-colors"
                :class="sortKey === col.key ? 'text-blue-600 dark:text-blue-400' : 'text-fg-muted dark:text-fg-muted-dark'"
              >
                <div class="flex items-center gap-1">
                  {{ col.label }}
                  <svg v-if="sortKey === col.key" class="h-3 w-3 flex-shrink-0 transition-transform" :class="sortDir === 'desc' ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 15l7-7 7 7" />
                  </svg>
                  <svg v-else class="h-3 w-3 flex-shrink-0 text-stroke-strong dark:text-fg-faint-dark opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-stroke dark:divide-stroke-dark">
            <tr v-if="isLoading">
              <td :colspan="activeColumns.length" class="px-4 py-10 text-center">
                <svg class="animate-spin h-6 w-6 mx-auto text-fg-faint" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </td>
            </tr>
            <tr v-else-if="filteredLessons.length === 0">
              <td :colspan="activeColumns.length" class="px-4 py-10 text-center text-sm text-fg-muted dark:text-fg-muted-dark">
                {{ lessons.length === 0 ? 'No lessons found' : 'No lessons match your filters' }}
              </td>
            </tr>
            <tr
              v-else
              v-for="row in filteredLessons"
              :key="row.id"
              class="hover:bg-surface-muted dark:hover:bg-gray-900 transition-colors"
            >
              <template v-for="col in activeColumns" :key="col.key">
                <td :class="['px-4 py-3 text-sm text-fg-secondary dark:text-fg-secondary-dark', col.tdClass]">

                  <!-- Date -->
                  <template v-if="col.key === 'lesson_date'">
                    <span class="whitespace-nowrap">{{ fmtLessonDate(row.lesson_date) }}</span>
                  </template>

                  <!-- Teacher -->
                  <template v-else-if="col.key === 'teacher_name'">
                    <span class="font-medium text-fg-base dark:text-fg-base-dark">{{ row.teacher_name || '—' }}</span>
                  </template>

                  <!-- Attendance status + student names -->
                  <template v-else-if="col.key === 'attendance_status'">
                    <div class="space-y-1">
                      <AttendanceStatusDropdown
                        :status="attendanceStatus(row)"
                        :label="attendanceStatus(row)"
                        :badge-class="attendanceBadgeClass(attendanceStatus(row))"
                        :is-too-far-future="isTooFarFuture(row)"
                        @select="status => onAttendanceSelect(row, status)"
                      />
                      <div v-if="row.attendance?.length" class="text-xs text-fg-muted dark:text-fg-muted-dark leading-snug">
                        {{ row.attendance.map(s => s.name).join(', ') }}
                      </div>
                    </div>
                  </template>

                  <!-- Duration -->
                  <template v-else-if="col.key === 'duration_minutes'">
                    {{ fmtDuration(row.duration_minutes) }}
                  </template>

                  <!-- Long text: truncate -->
                  <template v-else-if="col.long">
                    <span class="block truncate" :title="row[col.key] || ''">{{ row[col.key] || '—' }}</span>
                  </template>

                  <!-- Computed columns -->
                  <template v-else-if="col.compute">
                    {{ col.compute(row) }}
                  </template>

                  <!-- Generic -->
                  <template v-else>
                    {{ row[col.key] || '—' }}
                  </template>

                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useApi } from '../../composables/useApi.js'
import { lessonsChange } from '../../composables/useCalendarData.js'
import { useTableControls, DATE_OPS } from '../../composables/useTableControls.js'
import TableToolbar from '../common/TableToolbar.vue'
import AttendanceStatusDropdown from '../common/AttendanceStatusDropdown.vue'

const { fetchApi, markLessonAttended, markLessonExcused, markLessonAbsent, markLessonNotReported } = useApi()

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtLessonDate = dateStr => {
  if (!dateStr) return '—'
  try {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    })
  } catch { return dateStr }
}

const fmtDuration = mins => {
  if (!mins) return '—'
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h && m) return `${h}h ${m}m`
  return h ? `${h}h` : `${m}m`
}

const attendanceStatus = row => {
  const att = row.attendance || []
  if (!att.length) return 'not reported'
  const statuses = att.map(s => {
    const v = (s.attendance || s.status || '').toLowerCase()
    return v === 'snow day' ? 'excused' : v
  })
  if (statuses.every(s => s === 'attended'))  return 'attended'
  if (statuses.every(s => s === 'excused'))   return 'excused'
  if (statuses.some(s => s === 'absent'))     return 'absent'
  if (statuses.some(s => s === 'excused'))    return 'excused'
  if (statuses.every(s => s === 'scheduled')) return 'scheduled'
  return 'not reported'
}

const attendanceBadgeClass = status => ({
  'attended':     'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  'absent':       'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  'excused':      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
  'scheduled':    'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  'not reported': 'bg-surface-muted text-fg-secondary dark:bg-surface-muted-dark dark:text-fg-secondary-dark',
}[status] || 'bg-surface-muted text-fg-secondary')

// Mirrors backend guard (lessons.py _lesson_is_future): attended/absent are rejected
// when the lesson's date is more than 24h out, measured from lesson midnight.
const isTooFarFuture = row => {
  if (!row.lesson_date) return false
  const [y, m, d] = row.lesson_date.split('-').map(Number)
  return new Date(y, m - 1, d).getTime() > Date.now() + 24 * 60 * 60 * 1000
}

// The row's badge is one status for the whole lesson, so picking a new one applies
// it to every student on the lesson — the same bulk semantics the badge already implies.
const onAttendanceSelect = async (row, status) => {
  const students = (row.attendance || []).filter(s => s.name)
  if (!students.length) return

  const apiCall = {
    attended:      name => markLessonAttended(row.id, name),
    excused:       name => markLessonExcused(row.id, name),
    absent:        name => markLessonAbsent(row.id, name),
    'not reported': name => markLessonNotReported(row.id, name),
  }[status]
  if (!apiCall) return

  const previous = row.attendance
  // Mirror both keys the backend now writes (lessons.py update_lesson_attendance) —
  // attendanceStatus() above reads "attendance" first, falling back to "status".
  row.attendance = students.map(s => ({ ...s, status, attendance: status }))
  try {
    await Promise.all(students.map(s => apiCall(s.name)))
  } catch {
    row.attendance = previous
  }
}

const _dateSort = row => {
  if (!row.lesson_date) return 0
  const [y, m, d] = row.lesson_date.split('-').map(Number)
  return new Date(y, m - 1, d).getTime()
}

// ── Column definitions ────────────────────────────────────────────────────────
// Deliberately excludes: updated_at (Last Synced), created_at (First Scraped),
// msm_lesson_id, billing_decline_type, billed_at, price_cents (Our Price),
// billing_status — internal/billing fields, hidden from this dashboard for all users.
const COLUMNS = [
  {
    key: 'lesson_date', label: 'Date', default: true, tdClass: 'whitespace-nowrap',
    filterValue: row => fmtLessonDate(row.lesson_date),
    sortValue:   row => _dateSort(row),
    ops: DATE_OPS,
  },
  {
    key: 'lesson_time', label: 'Time', default: true, tdClass: 'whitespace-nowrap',
    sortValue: row => row.lesson_time || '',
  },
  { key: 'teacher_name', label: 'Teacher', default: true },
  { key: 'lesson_title', label: 'Student / Title', default: true, long: true, tdClass: 'max-w-[220px]' },
  {
    key: 'instrument', label: 'Instrument', default: true,
    ops: ['is', 'is not', 'contains', 'is empty', 'is not empty'],
  },
  {
    key: 'category', label: 'Category', default: false,
    ops: ['is', 'is not', 'contains'],
  },
  {
    key: 'duration_minutes', label: 'Duration', default: false, tdClass: 'whitespace-nowrap',
    sortValue: row => row.duration_minutes || 0,
    filterValue: row => fmtDuration(row.duration_minutes),
  },
  {
    key: 'attendance_status', label: 'Attendance', default: true, tdClass: 'min-w-[160px]',
    filterValue:  row => attendanceStatus(row),
    sortValue:    row => attendanceStatus(row),
    ops:          ['is', 'is not'],
    valueOptions: ['attended', 'absent', 'excused', 'scheduled', 'not reported'],
  },
  {
    key: 'location', label: 'Location', default: false,
    ops: ['contains', 'is empty', 'is not empty'],
  },
  {
    key: 'student_names', label: 'Students', default: false,
    filterValue: row => (row.attendance || []).map(s => s.name).join(', '),
    sortValue:   row => (row.attendance || []).map(s => s.name).join(', '),
    compute:     row => (row.attendance || []).map(s => s.name).join(', ') || '—',
  },
]

// ── Table controls ────────────────────────────────────────────────────────────
const {
  columnOrder, visibleKeys, activeColumns,
  resetColumns,
  sortKey, sortDir, toggleSort,
  searchQuery, activeFilters,
  filterRows,
} = useTableControls(
  COLUMNS,
  'my_lessons_list_columns_v1',
  (row, q) =>
    row.lesson_title?.toLowerCase().includes(q) ||
    row.instrument?.toLowerCase().includes(q)   ||
    row.category?.toLowerCase().includes(q)
)

// ── Data ──────────────────────────────────────────────────────────────────────
const lessons    = ref([])
const isLoading  = ref(false)

const filteredLessons = computed(() => filterRows(lessons.value))

let refetchQueued = false
const fetchLessons = async () => {
  if (isLoading.value) { refetchQueued = true; return }
  isLoading.value = true
  try {
    lessons.value = await fetchApi('/api/lessons/list')
  } catch {
    console.error('Failed to fetch lesson list')
  } finally {
    isLoading.value = false
    if (refetchQueued) { refetchQueued = false; fetchLessons() }
  }
}
watch(lessonsChange, () => fetchLessons())

// Current Sunday–Saturday week, expressed as exclusive after/before bounds
// (the day before Sunday / the day after Saturday) since DATE_OPS has no
// inclusive "between" operator.
const currentWeekFilters = () => {
  const iso = d => d.toISOString().split('T')[0]
  const now = new Date()
  const sunday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay())
  const dayBeforeSunday = new Date(sunday); dayBeforeSunday.setDate(sunday.getDate() - 1)
  const dayAfterSaturday = new Date(sunday); dayAfterSaturday.setDate(sunday.getDate() + 7)
  return [
    { col: 'lesson_date', op: 'after',  value: iso(dayBeforeSunday) },
    { col: 'lesson_date', op: 'before', value: iso(dayAfterSaturday) },
  ]
}

// Filters persist for the tab session (survives refresh/app-switch, resets on tab
// close) so a manually-widened range doesn't keep snapping back to the current week.
const FILTERS_STORAGE_KEY = 'my_lessons_list_filters_v1'

onMounted(() => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY))
    activeFilters.value = Array.isArray(stored) ? stored : currentWeekFilters()
  } catch {
    activeFilters.value = currentWeekFilters()
  }
  fetchLessons()
})

watch(activeFilters, (val) => {
  sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(val))
}, { deep: true })
</script>
